/**
 * Meta (Facebook) Pixel loading and standard-event tracking.
 *
 * The pixel is loaded lazily — `components/analytics/DeferredAnalytics` waits for
 * the first interaction (or browser idle) so it never competes with LCP. Events
 * can therefore be raised before `window.fbq` exists, so everything tracked here
 * is buffered and flushed once the pixel is live. High-intent events additionally
 * force the load rather than waiting on the idle timer. `Purchase` is reported
 * from the backend, not from this client pixel.
 */

/**
 * Meta only runs on the production host, so dev / QA (user-dev, user-qa) and
 * Vercel previews never load the pixel or buffer events for it. Mirrors
 * `ANALYTICS_ALLOWED_HOSTS` in `app/layout.tsx`, which gates the server render.
 */
const ALLOWED_ORIGINS = new Set([
  'https://exaltride.com',
  'https://www.exaltride.com',
])

/** Anything raised before the pixel loads waits here. Bounded so a misconfigured
 *  pixel id can't grow the buffer without limit. */
const MAX_BUFFERED_EVENTS = 20

const DEFAULT_CURRENCY = 'INR'

/**
 * Meta matches `content_ids` against the product catalog feed to drive dynamic
 * product ads and retargeting. `product` treats each id as an individual item;
 * `product_group` treats it as a parent with variants. This must agree with how
 * the catalog feed is built — see `resolveMetaContentId` below.
 */
const CONTENT_TYPE = 'product'

type MetaPixelWindow = Window & {
  __exaltrideMetaPixelLoaded?: boolean
  fbq?: (...args: unknown[]) => void
  _fbq?: unknown
  dataLayer?: Record<string, unknown>[]
}

type MetaEventParams = Record<string, unknown>

type BufferedEvent = {
  name: string
  params: MetaEventParams
  eventId: string
  /** Meta custom events use `fbq('trackCustom', …)` instead of `track`. */
  custom?: boolean
}

/** One line of a cart / order / product for the event payload. */
export type MetaLineItem = {
  productId: string
  sku?: string | null
  /**
   * Variant-level SKU. Deliberately left unpopulated for now: cart lines only
   * carry the parent SKU, so filling it in on one event alone would split the
   * funnel. Plumb it through every call site at once, or not at all.
   */
  variantSku?: string | null
  price: number
  quantity: number
}

let configuredPixelId: string | null = null
const bufferedEvents: BufferedEvent[] = []

/**
 * The identifier Meta matches against the catalog feed's `id` column.
 *
 * Defaults to the SKU (variant SKU first, since a feed that lists variants
 * separately keys on it). If the feed keys on something else, this function is
 * the only place that has to change — events fire correctly either way, but a
 * mismatch silently matches zero catalog items and dynamic ads stay empty.
 */
export function resolveMetaContentId(item: MetaLineItem): string {
  return item.variantSku || item.sku || item.productId
}

function isMetaPixelAllowed(): boolean {
  return (
    typeof window !== 'undefined' && ALLOWED_ORIGINS.has(window.location.origin)
  )
}

export function isMetaPixelLoaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as MetaPixelWindow).fbq === 'function'
  )
}

/**
 * Records the pixel id for later loads. Deliberately does not load the pixel —
 * the deferral is what keeps it off the critical path — but it does flush
 * anything raised before the id was known.
 */
export function configureMetaPixel(pixelId: string | null): void {
  configuredPixelId = pixelId

  if (pixelId && bufferedEvents.length > 0) {
    loadMetaPixel()
    flushBufferedEvents()
  }
}

/** Idempotent. Returns whether `window.fbq` is available to receive events. */
export function loadMetaPixel(): boolean {
  if (!isMetaPixelAllowed() || !configuredPixelId) {
    return false
  }

  const analyticsWindow = window as MetaPixelWindow

  if (analyticsWindow.__exaltrideMetaPixelLoaded) {
    return true
  }

  analyticsWindow.__exaltrideMetaPixelLoaded = true

  const fbq = function (...args: unknown[]) {
    fbq.queue.push(args)
  } as ((...args: unknown[]) => void) & {
    queue: unknown[]
    loaded: boolean
    version: string
  }

  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  analyticsWindow.fbq = fbq
  analyticsWindow._fbq = fbq

  const metaScript = document.createElement('script')
  metaScript.async = true
  metaScript.src = 'https://connect.facebook.net/en_US/fbevents.js'

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(metaScript, firstScript)
  } else {
    // No script tag to anchor to; without this the stub below would queue
    // events that nothing ever drains.
    document.head.appendChild(metaScript)
  }

  fbq('init', configuredPixelId)
  fbq('track', 'PageView')

  return true
}

/**
 * A per-event id so the same conversion sent from the browser and from the
 * Conversions API collapses into one. Harmless until CAPI is wired up.
 */
function createEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function sendToPixel({ name, params, eventId, custom }: BufferedEvent): void {
  const analyticsWindow = window as MetaPixelWindow
  const method = custom ? 'trackCustom' : 'track'
  analyticsWindow.fbq?.(method, name, params, { eventID: eventId })
}

/** Same id GTM / CAPI must send as `event_id` so Meta can drop the duplicate. */
function pushDataLayerEvent({
  name,
  params,
  eventId,
  custom,
}: BufferedEvent): void {
  const analyticsWindow = window as MetaPixelWindow
  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? []
  analyticsWindow.dataLayer.push({
    event: 'meta_pixel_event',
    event_name: name,
    event_id: eventId,
    event_custom: Boolean(custom),
    ...params,
  })
}

function flushBufferedEvents(): void {
  if (bufferedEvents.length === 0 || !isMetaPixelLoaded()) {
    return
  }

  // Drain before sending so a throw inside fbq can't replay the queue.
  bufferedEvents.splice(0, bufferedEvents.length).forEach(sendToPixel)
}

type TrackOptions = {
  /**
   * Load the pixel now instead of waiting for the deferred trigger. On for the
   * high-intent events; off for PageView, where forcing a load would defeat the
   * deferral for a shopper who bounces.
   */
  forceLoad?: boolean
  /** Use Meta's custom-event channel (`trackCustom`) instead of a standard event. */
  custom?: boolean
}

/** Returns the `eventID` sent to Meta, for later CAPI deduplication. */
export function trackMetaEvent(
  name: string,
  params: MetaEventParams = {},
  options: TrackOptions = {},
): string | null {
  if (!isMetaPixelAllowed()) {
    return null
  }

  const { forceLoad = true, custom = false } = options

  if (!isMetaPixelLoaded() && !forceLoad) {
    return null
  }

  if (bufferedEvents.length >= MAX_BUFFERED_EVENTS) {
    return null
  }

  const eventId = createEventId()
  const bufferedEvent = { name, params, eventId, custom }
  bufferedEvents.push(bufferedEvent)
  pushDataLayerEvent(bufferedEvent)

  if (forceLoad) {
    loadMetaPixel()
  }

  flushBufferedEvents()

  return eventId
}

function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100
}

/**
 * `content_ids` / `contents` / `value` / `num_items` for a set of lines, in the
 * shape Meta's standard commerce events expect.
 */
function buildContentPayload(items: MetaLineItem[]) {
  const contents = items.map((item) => ({
    id: resolveMetaContentId(item),
    quantity: item.quantity,
    item_price: roundCurrency(item.price),
  }))

  return {
    content_type: CONTENT_TYPE,
    content_ids: contents.map((content) => content.id),
    contents,
    num_items: items.reduce((total, item) => total + item.quantity, 0),
    value: roundCurrency(
      items.reduce((total, item) => total + item.price * item.quantity, 0),
    ),
    currency: DEFAULT_CURRENCY,
  }
}

export function trackMetaPageView(): void {
  // No forceLoad: the initial PageView already ships with `fbq('init')`, and a
  // shopper who never interacts shouldn't pull the pixel in on navigation alone.
  trackMetaEvent('PageView', {}, { forceLoad: false })
}

export function trackMetaViewContent(
  item: MetaLineItem & { title?: string; category?: string },
): void {
  trackMetaEvent('ViewContent', {
    ...buildContentPayload([item]),
    content_name: item.title,
    content_category: item.category,
  })
}

export function trackMetaAddToCart(
  item: MetaLineItem & { title?: string },
): void {
  trackMetaEvent('AddToCart', {
    ...buildContentPayload([item]),
    content_name: item.title,
  })
}

export function trackMetaAddToWishlist(
  item: MetaLineItem & { title?: string },
): void {
  trackMetaEvent('AddToWishlist', {
    ...buildContentPayload([item]),
    content_name: item.title,
  })
}

/**
 * Distinct from cart "Proceed to Checkout". Buy Now still also fires
 * `InitiateCheckout` so Meta's standard funnel stays complete; this custom
 * event lets marketing measure Buy Now clicks separately.
 */
export function trackMetaBuyNow(
  item: MetaLineItem & { title?: string },
): void {
  trackMetaEvent(
    'BuyNow',
    {
      ...buildContentPayload([item]),
      content_name: item.title,
    },
    { custom: true },
  )
}

export function trackMetaInitiateCheckout(
  items: MetaLineItem[],
  /** Cart total after coupon / shipping when known, so it matches what's charged. */
  overrideValue?: number,
): void {
  const payload = buildContentPayload(items)

  trackMetaEvent('InitiateCheckout', {
    ...payload,
    value:
      typeof overrideValue === 'number'
        ? roundCurrency(overrideValue)
        : payload.value,
  })
}
